import { formatUtcLocal } from "../../../utils/dateFormat";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Paper,
  Stack,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  DataGridPro,
  GridColDef,
  GridValueFormatterParams,
} from "@mui/x-data-grid-pro";
import { Helmet } from "react-helmet-async";
import {
  useUploadDealerImportMutation,
  useListImportJobsQuery,
  useLazyDownloadTemplateQuery,
} from "../../../services/API/dealerImportApiSlice";
import { JobSummary } from "../../../types/dealerImportTypes";

export default function DealerImportPage() {
  const fileRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [upload, { isLoading }] = useUploadDealerImportMutation();
  const { data: jobs = [], refetch, isFetching } = useListImportJobsQuery();
  const [triggerDownload] = useLazyDownloadTemplateQuery();

  const downloadTemplate = async () => {
    setError(null);
    try {
      const text = await triggerDownload().unwrap();
      const blob = new Blob([text], { type: "text/csv" });
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = "RoWriterDealerImportTemplate.csv";
      a.click();
      URL.revokeObjectURL(objectUrl);
    } catch {
      setError("Failed to download template.");
    }
  };

  const onUpload = async () => {
    setError(null);
    const file = fileRef.current?.files?.[0];
    if (!file) {
      setError("Choose a CSV file first.");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    try {
      const res = await upload(form).unwrap();
      navigate(`/dashboard/dealer-import/${res.jobId}`);
    } catch (e: any) {
      setError(typeof e?.data === "string" ? e.data : "Upload failed.");
    }
  };

  const cols: GridColDef<JobSummary>[] = [
    {
      field: "uploadedAt",
      headerName: "Uploaded",
      flex: 1,
      valueFormatter: (params: GridValueFormatterParams) =>
        formatUtcLocal(params.value as string),
    },
    { field: "sourceFileName", headerName: "File", flex: 1.5 },
    { field: "uploadedBy", headerName: "By", flex: 1 },
    { field: "status", headerName: "Status", flex: 0.6 },
    { field: "totalRows", headerName: "Rows", flex: 0.4, type: "number" },
    { field: "createdRows", headerName: "Created", flex: 0.4, type: "number" },
    { field: "updatedRows", headerName: "Updated", flex: 0.4, type: "number" },
    {
      field: "unchangedRows",
      headerName: "Unchanged",
      flex: 0.5,
      type: "number",
    },
    { field: "failedRows", headerName: "Failed", flex: 0.4, type: "number" },
    {
      field: "completedAt",
      headerName: "Completed",
      flex: 1,
      valueFormatter: (params: GridValueFormatterParams) =>
        formatUtcLocal(params.value as string),
    },
  ];

  return (
    <>
      <Helmet>
        <title>RO Writer Dealer Import</title>
      </Helmet>
      <Stack spacing={3} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom style={{ fontWeight: "bold" }}>
          RO Writer Dealer Import
        </Typography>

        <Paper sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Typography variant="h6">Upload a batch</Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button variant="outlined" onClick={downloadTemplate}>
                Download CSV template
              </Button>
              <input type="file" accept=".csv,.xlsx" ref={fileRef} />
              <Button variant="contained" onClick={onUpload} disabled={isLoading}>
                {isLoading ? <CircularProgress size={18} /> : "Upload"}
              </Button>
              <Button onClick={() => refetch()}>Refresh list</Button>
            </Stack>
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </Paper>

        <Paper sx={{ p: 2, height: 600 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Recent jobs
          </Typography>
          <Box sx={{ height: 520 }}>
            <DataGridPro
              rows={jobs}
              columns={cols}
              getRowId={(r) => r.jobId}
              loading={isFetching}
              onRowClick={(p) => navigate(`/dashboard/dealer-import/${p.id}`)}
              disableSelectionOnClick
            />
          </Box>
        </Paper>
      </Stack>
    </>
  );
}
